import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "@/hooks/use-auth";
import { t } from "@/i18n/pl";
import { ForcePasswordPage } from "@/pages/force-password-page";

/**
 * Gate for authenticated routes.
 *
 * This is a usability guard, not a security boundary — the bundle is public and
 * anyone can route around it. Every protected resource is enforced server-side
 * (including the must-change-password lock, which the API applies to every
 * endpoint except the password-change flow).
 */
export function ProtectedRoute() {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  // Render nothing until the refresh-cookie exchange settles, otherwise a
  // signed-in user is briefly redirected to /login on every reload.
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground text-sm">{t.common.loading}</p>
      </div>
    );
  }

  if (!user) {
    // Remember where they were headed so login can send them back.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // First login on an admin-issued password: nothing else renders until the
  // employee sets their own. No route escapes this — the API enforces it too.
  if (user.must_change_password) {
    return <ForcePasswordPage user={user} />;
  }

  return <Outlet />;
}
